import { LockOutlined } from '@ant-design/icons';
import { isApolloError } from '@apollo/client';
import { useTitle } from 'ahooks';
import { Button, Col, Form, FormProps, Input, Row, Typography } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useAuthentication } from '../../../components/AuthenticationProvider';
import useRedirect from '../../../components/AuthenticationProvider/useRedirect';
import EnhancedForm from '../../../components/EnhancedForm';
import AuthException, {
  AuthExceptionMessage,
} from '../../../constants/AuthException';
import paths from '../../../paths.json';
import { useSearchQuery } from '../../../utils/location';

import styles from './styles.module.less';

const title = 'Change Password';

type FormValues = {
  newPassword: string;
  newPasswordConfirm: string;
};

const ChangePasswordForm: FC<FormProps<FormValues>> = (props) => {
  useTitle(title);
  const history = useHistory();
  const location = useLocation<{ oldPassword: string; username: string }>();
  const redirect = useRedirect();
  const { changePassword, resetPassword } = useAuthentication();
  const { searchQuery } = useSearchQuery<{ token?: string; email?: string }>();
  const [state] = useState(location.state);

  const [form] = Form.useForm();
  const newPassword = Form.useWatch<string | undefined>('newPassword', form);

  const { token, email } = searchQuery;

  useEffect(() => {
    if (state) {
      history.replace({ state: undefined });
    }
  }, [state, history]);

  if ((!state?.oldPassword || !state?.username) && (!token || !email)) {
    history.replace(`${paths.login}`);
  }

  const onFinish = useCallback(
    async (values: FormValues) => {
      try {
        if (token && email) {
          await resetPassword(email, values.newPasswordConfirm, token);
        } else {
          await changePassword({
            ...values,
            ...state,
          });
        }
      } catch (e) {
        let message =
          'Critical error during password change. Please try again.';
        if (isApolloError(e as Error)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const type = e.graphQLErrors?.[0]?.type as AuthException;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          message = (type && AuthExceptionMessage[type]) || e.message;
        }

        history.replace(`${paths.changePassword}`, {
          error: message,
        });

        return;
      }

      redirect();
    },
    [changePassword, email, history, redirect, resetPassword, state, token],
  );

  return (
    <Row gutter={[8, 28]}>
      <Col xs={24}>
        <Row align="middle" gutter={[8, 8]} justify="center">
          <Col xs={24}>
            <Typography.Title className={styles.title} level={3}>
              {title}
            </Typography.Title>
          </Col>
          <Col xs={24}>
            <Typography.Title
              className={styles.title}
              level={5}
              type="secondary"
            >
              Before letting you in, we need to ask you to change your password.
              By changing your password, you help make sure that only you can
              use your account.
            </Typography.Title>
          </Col>
        </Row>
      </Col>
      <Col xs={24}>
        <EnhancedForm {...props} form={form} onFinish={onFinish}>
          <Form.Item name="newPassword" rules={[{ required: true }]}>
            <Input.Password
              placeholder="New Password"
              prefix={<LockOutlined />}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="newPasswordConfirm"
            rules={[
              { required: true },
              {
                validator: async (_, newPasswordConfirm) => {
                  if (newPassword !== newPasswordConfirm) {
                    return Promise.reject("Passwords don't match");
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              placeholder="Confirm New Password"
              prefix={<LockOutlined />}
              size="large"
            />
          </Form.Item>

          <Row align="middle" gutter={[8, 16]} justify="center">
            <Col xs={24}>
              <Button block htmlType="submit" size="large" type="primary">
                Change Password
              </Button>
            </Col>
            <Col>
              <Link to={paths.login}>
                <Button type="link">Back to Login?</Button>
              </Link>
            </Col>
          </Row>
        </EnhancedForm>
      </Col>
    </Row>
  );
};

ChangePasswordForm.displayName = 'ChangePasswordForm';

export default ChangePasswordForm;
